import { debug, warn, type Codec } from '@4players/odin-common';
import { Channel as TauriChannel } from '@tauri-apps/api/core';
import { WebGLVideoTarget } from '../render/webgl-target';
import { LogGate } from '../../log-gate';

export type FrameKind = 'config' | 'key' | 'delta';

/** Wire format for the per-stream encoded-frame channel from Rust.
 *  Tauri 2's typed `Channel<T>` defaults to JSON, which would inflate
 *  every byte of `data` into ~4 chars of text — at 1080p H264 that's
 *  hundreds of KB of JSON per frame and turned into multi-second IPC
 *  backlogs. The Rust side now sends an `InvokeResponseBody::Raw`
 *  (see `EncodedFrameMsg` in `video::types`) which lands on the JS
 *  side as a plain `ArrayBuffer`. `decodeWireFrame` parses the small
 *  header + slices off the codec payload as a zero-copy `Uint8Array`. */
export interface NativeEncodedFrame {
  kind: FrameKind;
  codec: Codec;
  /** Microseconds, anchored to wallclock for AV-sync against the
   *  audio path. Forwarded verbatim into `EncodedVideoChunk`. */
  timestamp_us: number;
  data: Uint8Array;
}

/** Wire-format binary header layout — must match `EncodedFrameMsg` in
 *  `src-tauri/plugins/odin/src/video/types.rs`. */
const WIRE_HEADER_LEN = 10;
const WIRE_KIND: Record<number, FrameKind> = { 0: 'config', 1: 'key', 2: 'delta' };
const WIRE_CODEC: Record<number, Codec> = { 0: 'H264', 1: 'VP8', 2: 'VP9', 3: 'AV1' };

export function decodeWireFrame(buf: ArrayBuffer): NativeEncodedFrame {
  const view = new DataView(buf);
  const kind = WIRE_KIND[view.getUint8(0)] ?? 'delta';
  const codec = WIRE_CODEC[view.getUint8(1)] ?? 'H264';
  // BigUint64 → Number is safe up to 2^53; timestamp_us tops out at
  // a few hours of stream which is well below.
  const timestamp_us = Number(view.getBigUint64(2, true));
  return {
    kind,
    codec,
    timestamp_us,
    data: new Uint8Array(buf, WIRE_HEADER_LEN),
  };
}

interface PendingChunk {
  type: 'key' | 'delta';
  timestamp: number;
  data: Uint8Array;
}

/** Side-channel tag added to the receiver's stub MediaStream so any
 *  consumer holding the stream (voice-view, app-settings, the ODIN
 *  tauri-plugin) can locate the receiver and either mount a canvas or
 *  read native-side metadata, without the native capture API having to know
 *  about each consumer. Mirrors the existing `__odinNativeHandle`
 *  pattern used by the tauri-plugin to detect native-backed streams. */
interface NativeStreamTags {
  __odinNativeHandle?: number;
  __odinFramerate?: number;
  __odinNativeReceiver?: NativeVideoReceiver;
}

/** Returns the `NativeVideoReceiver` backing a MediaStream produced
 *  by the native capture API on the Tauri path, or `null` for getUserMedia
 *  streams in the web build. Use this to decide whether a component
 *  should render via canvas mount or `<video [srcObject]>`. */
export function nativeReceiverOf(
  stream: MediaStream | null | undefined,
): NativeVideoReceiver | null {
  if (!stream) return null;
  return (stream as unknown as NativeStreamTags).__odinNativeReceiver ?? null;
}

/** Wraps a Tauri Channel of encoded frames into something a `<canvas>`
 *  element can render. The receiver owns the WebCodecs `VideoDecoder`
 *  and keeps the most recently decoded `VideoFrame`; consumers register
 *  a target canvas via `mountTo(canvas)` and the receiver paints every
 *  decoded frame onto each registered target.
 *
 *  Skipping the previous CanvasSink → captureStream() → MediaStream →
 *  `<video [srcObject]>` chain cuts ~2 render hops per frame, which is
 *  the dominant source of perceived preview lag on WebKitGTK (where
 *  the chain runs on the CPU compositor). */
export class NativeVideoReceiver {
  private decoder: VideoDecoder | null = null;
  private currentCodec: Codec | null = null;
  private decoderConfig: VideoDecoderConfig | null = null;
  private pending: PendingChunk[] = [];
  private closed = false;
  private onKeyframeNeeded: (() => void) | null = null;
  private keyframeRequestPending = false;
  /** Decode/paint failures repeat per chunk or frame while a stream is
   *  broken — sampled per receiver, keyed by the failing step. */
  private readonly failureLog = new LogGate();
  /** When true, we've detected the decoder falling behind and asked the
   *  sender to emit a keyframe. Until that key arrives, every delta is
   *  dropped — feeding deltas onto a partially-queued GOP either bloats
   *  the queue further or produces garbage frames (deltas reference
   *  earlier frames that may already have been pruned by the decoder
   *  internally when we hit the queue cap). The next `key` clears it. */
  private discardingUntilKey = false;
  /** In-flight threshold for backpressure. Anything above this is a
   *  pipeline backlog the decoder is unlikely to recover from on its
   *  own: WebKit's WebCodecs decoder accepts `decode()` calls eagerly
   *  (`decodeQueueSize` drops to 0 the moment a decode starts) but its
   *  internal frame pipeline can hold many frames before producing
   *  output. We track in-flight ourselves by incrementing on each
   *  `decode()` and decrementing in the output callback — that's the
   *  real signal for "decoder is falling behind".
   *
   *  Threshold value tradeoff:
   *  - Too low (e.g. 3): the threshold trips constantly on machines
   *    where the decoder runs slower than realtime, so we cycle
   *    drop → request-key → drop → request-key and the preview goes
   *    black (only keyframes ever get through).
   *  - Too high (e.g. 30): we tolerate seconds of structural lag and
   *    waste decode work on frames that will never be rendered.
   *
   *  6 frames at 15 fps = ~400 ms of slack. Down from 12 — the
   *  previous threshold tolerated ~800 ms of pipeline drift on slow
   *  hosts (notably WebKitGTK without VAAPI, where decoding can
   *  silently fall behind without the in-flight queue ever
   *  draining), which translates to sustained CPU/GPU work on
   *  frames that arrive too late to matter. Drop earlier, request a
   *  fresh keyframe, resync. The "decoder is too slow for this
   *  stream" failure mode is still recoverable by lowering encode
   *  resolution / framerate. */
  private static readonly IN_FLIGHT_BACKPRESSURE_THRESHOLD = 6;
  /** Frames passed to `decoder.decode()` but whose output callback has
   *  not yet fired. See the threshold doc for why this rather than the
   *  built-in `decodeQueueSize`. */
  private inFlightCount = 0;
  /** Most recently decoded frame. Kept alive so that newly mounted
   *  canvases can show something immediately instead of staying blank
   *  until the next decoder output. Replaced (and closed) on every
   *  new decode. */
  private latestFrame: VideoFrame | null = null;
  /// Mount targets keyed by their `<canvas>` element. The value is a
  /// `WebGLVideoTarget` when WebGL setup succeeded (the common case)
  /// or `null` if `getContext('webgl')` returned null on that
  /// canvas — we then fall back to the original 2D drawImage path
  /// for just that target. WebGL gives us GPU-backed compositing on
  /// WebKitGTK (Linux native) where the 2D path is CPU-Cairo-bound,
  /// and consistent zero-copy upload on Chromium / WKWebView too.
  private readonly targets = new Map<HTMLCanvasElement, WebGLVideoTarget | null>();
  /** When the host window goes to `document.visibilityState === 'hidden'`
   *  (minimized, hidden behind tray, tab in the background for the web
   *  build) the decoded output isn't on screen anyway. We keep the
   *  decoder configured but drop every incoming frame to skip the
   *  decode + paint work. On resume we request a fresh keyframe so the
   *  next IDR re-seeds the GOP — without it the receiver would have to
   *  wait up to a full keyframe interval (2 s) for an organically-
   *  emitted IDR before any usable frame lands. */
  private paused = false;
  private visibilityListener: (() => void) | null = null;
  /** Empty MediaStream that exists only to carry the side-channel tags
   *  (`__odinNativeHandle`, `__odinFramerate`, `__odinNativeReceiver`)
   *  the rest of the pipeline expects. Has no tracks — `<video>` won't
   *  render it. Consumers use `mountTo` to display the actual frames. */
  readonly stream: MediaStream;

  constructor() {
    if (typeof VideoDecoder === 'undefined') {
      throw new Error('VideoDecoder unavailable — WebCodecs required');
    }
    this.stream = new MediaStream();
    if (typeof document !== 'undefined') {
      this.paused = document.visibilityState === 'hidden';
      const listener = () => this.setPaused(document.visibilityState === 'hidden');
      document.addEventListener('visibilitychange', listener);
      this.visibilityListener = listener;
    }
  }

  private setPaused(paused: boolean): void {
    if (this.paused === paused || this.closed) return;
    this.paused = paused;
    if (paused) {
      // Drop anything we'd otherwise be about to decode — the GOP we'll
      // resume into is the one starting at the next requested keyframe.
      this.pending = [];
      this.discardingUntilKey = true;
    } else {
      // Force-IDR so the decoder gets a clean restart point instead of
      // waiting for the next natural keyframe.
      this.requestKeyframe();
    }
  }

  /** Invoked when the decoder hits an error or a decode throws.
   *  Callers wire this to a tauri invoke of `webrtc_video_request_keyframe`
   *  so the sender flushes a fresh IDR and the GOP becomes
   *  decodable again. Coalesced so a burst of decoder errors emits
   *  at most one keyframe request per ~1s window. */
  setKeyframeRequester(fn: () => void): void {
    this.onKeyframeNeeded = fn;
  }

  private requestKeyframe(): void {
    if (!this.onKeyframeNeeded || this.keyframeRequestPending || this.closed) return;
    this.keyframeRequestPending = true;
    try {
      this.onKeyframeNeeded();
    } catch (e) {
      const repeat = this.failureLog.hit('keyframe');
      if (repeat !== null)
        warn(`[native-video-receiver] keyframe request failed${repeat}`, { err: e });
    }
    setTimeout(() => {
      this.keyframeRequestPending = false;
    }, 1000);
  }

  /** Register a canvas as a paint target. Every decoded frame is
   *  drawn onto it (canvas dims are kept in sync with the source).
   *  Returns an unmount function — callers should invoke it on
   *  component destroy.
   *
   *  If a frame is already buffered, paints it once synchronously so
   *  late mounters (e.g. a settings panel opened mid-stream) don't
   *  flash blank until the next decoder output. */
  mountTo(canvas: HTMLCanvasElement): () => void {
    if (this.closed) return () => {};
    // Build the WebGL target eagerly so we know upfront whether the
    // canvas can take a GL context. `getContext` is one-shot per
    // context family — if WebGL fails, the canvas must stay 2D for
    // its entire lifetime, so we have to commit on first mount.
    let target: WebGLVideoTarget | null = null;
    try {
      target = new WebGLVideoTarget(canvas);
    } catch (e) {
      warn('[native-video-receiver] WebGL target unavailable, using 2D drawImage fallback', {
        err: e,
      });
      target = null;
    }
    this.targets.set(canvas, target);
    if (this.latestFrame) {
      this.paintTo(canvas, target, this.latestFrame);
    }
    return () => {
      const t = this.targets.get(canvas);
      if (t) t.dispose();
      this.targets.delete(canvas);
    };
  }

  /** Wire a Tauri Channel from `invoke<...>('...')` to this receiver.
   *  The Rust side sends `InvokeResponseBody::Raw` so each message
   *  arrives as an `ArrayBuffer`; we parse the binary wire format and
   *  hand off the resulting frame. */
  attach(channel: TauriChannel<ArrayBuffer>): void {
    channel.onmessage = (msg) => this.push(decodeWireFrame(msg));
  }

  push(frame: NativeEncodedFrame): void {
    if (this.closed) return;
    // While paused (window hidden / minimized) we still receive frames
    // from the Tauri channel, but nothing renders them. Drop the work
    // here so the decoder isn't doing CPU/GPU for output nobody sees.
    // `config` frames carry the SPS/PPS — we toss them too because the
    // sender emits a fresh config on every IDR, and we explicitly ask
    // for one on resume, so there's nothing to preserve.
    if (this.paused) return;
    const data = frame.data;

    if (frame.kind === 'config') {
      debug('[native-video-receiver] config frame', {
        codec: frame.codec,
        descBytes: data.byteLength,
      });
      this.configureDecoder(frame.codec, data);
      return;
    }

    const chunk: PendingChunk = {
      type: frame.kind === 'key' ? 'key' : 'delta',
      timestamp: frame.timestamp_us,
      data,
    };

    if (!this.decoder || this.decoder.state !== 'configured') {
      // First data frame may arrive before the decoder is configured
      // when there's no explicit config-frame (VP8/VP9/AV1 carry their
      // params inline in the keyframe). Stash it; flushPending() runs
      // as soon as configure resolves.
      if (frame.kind === 'key' && !this.decoderConfig) {
        this.configureFromKeyframe(frame.codec);
      }
      this.pending.push(chunk);
      this.flushPending();
      return;
    }

    this.decode(chunk);
  }

  private configureDecoder(codec: Codec, description: Uint8Array): void {
    // The encoder side emits Config on every IDR so a receiver that's
    // landed in `closed` state after a decode error can reconfigure
    // mid-stream. In the steady-state local-preview case the SPS/PPS
    // bytes never change — rebuilding the WebCodecs decoder on each
    // IDR closes a live decoder with in-flight decodes whose output
    // callbacks never fire on the new instance, leaving `inFlightCount`
    // climbing monotonically until the backpressure cap (12) trips
    // permanently and every delta gets dropped. Skip the rebuild when
    // the codec + descriptor match what's currently configured.
    if (
      this.decoder?.state === 'configured' &&
      this.currentCodec === codec &&
      bytesEqual(this.decoderConfig?.description, description)
    ) {
      return;
    }
    const config = buildDecoderConfig(codec, description);
    this.decoderConfig = config;
    this.currentCodec = codec;
    this.installDecoder(config);
    this.flushPending();
  }

  private configureFromKeyframe(codec: Codec): void {
    if (this.currentCodec === codec && this.decoder?.state === 'configured') return;
    const config = buildDecoderConfig(codec, undefined);
    this.decoderConfig = config;
    this.currentCodec = codec;
    this.installDecoder(config);
  }

  private installDecoder(config: VideoDecoderConfig): void {
    if (this.decoder) {
      try {
        this.decoder.close();
      } catch {
        // ignore
      }
    }
    // Closing the old decoder cancels its pending decodes — their
    // output callbacks never fire, so the in-flight bookkeeping the
    // backpressure check relies on becomes stale. Reset it so the
    // fresh decoder starts clean. `discardingUntilKey` likewise can't
    // expect the *old* decoder's "next key" — clear it; the upcoming
    // `flushPending` will gate on the next key it finds in `pending`.
    this.inFlightCount = 0;
    this.discardingUntilKey = false;
    let firstFrameLogged = false;
    this.decoder = new VideoDecoder({
      output: (frame) => {
        if (!firstFrameLogged) {
          firstFrameLogged = true;
          debug('[native-video-receiver] first decoded frame', {
            codedWidth: frame.codedWidth,
            codedHeight: frame.codedHeight,
            format: frame.format,
            ts: frame.timestamp,
          });
        }
        this.onFrame(frame);
      },
      error: (err) => {
        const repeat = this.failureLog.hit('decoder');
        if (repeat !== null) warn(`[native-video-receiver] decoder error${repeat}`, { err });
        this.requestKeyframe();
      },
    });
    debug('[native-video-receiver] configuring decoder', { config });
    try {
      this.decoder.configure(config);
    } catch (e) {
      warn('[native-video-receiver] configure failed', { err: e });
    }
  }

  private flushPending(): void {
    if (!this.decoder || this.decoder.state !== 'configured') return;
    // Drop pending delta frames before the first keyframe — feeding a
    // delta into a freshly-configured decoder produces garbage frames
    // until the next keyframe lands.
    let firstKey = -1;
    for (let i = 0; i < this.pending.length; i++) {
      if (this.pending[i].type === 'key') {
        firstKey = i;
        break;
      }
    }
    const drained = firstKey >= 0 ? this.pending.slice(firstKey) : [];
    this.pending = [];
    for (const chunk of drained) this.decode(chunk);
  }

  private decode(chunk: PendingChunk): void {
    if (!this.decoder) return;

    // Hold-off mode: we recently asked for a keyframe because the
    // decoder was behind. Drop everything until the key lands so we
    // don't keep feeding deltas onto an already-backlogged queue.
    if (this.discardingUntilKey) {
      if (chunk.type !== 'key') return;
      this.discardingUntilKey = false;
    }

    // Backpressure: if too many frames are already in flight (decoded
    // but no output yet) this one would just deepen the backlog —
    // dropped frames are strictly better than a preview that drifts
    // multiple seconds behind reality. Drop deltas; let keys through
    // because dropping a key would leave us stuck in hold-off until
    // the next natural one (whole GOP interval).
    if (
      chunk.type === 'delta' &&
      this.inFlightCount >= NativeVideoReceiver.IN_FLIGHT_BACKPRESSURE_THRESHOLD
    ) {
      const repeat = this.failureLog.hit('backlog');
      if (repeat !== null) {
        warn(
          `[native-video-receiver] decoder pipeline backlog, dropping delta and requesting keyframe${repeat}`,
          { inFlight: this.inFlightCount },
        );
      }
      this.discardingUntilKey = true;
      this.requestKeyframe();
      return;
    }

    try {
      this.inFlightCount++;
      this.decoder.decode(
        new EncodedVideoChunk({
          type: chunk.type,
          timestamp: chunk.timestamp,
          data: chunk.data,
        }),
      );
    } catch (e) {
      // decode() throws sync only on configure mismatch — back out the
      // in-flight bump so the counter doesn't drift permanently.
      this.inFlightCount = Math.max(0, this.inFlightCount - 1);
      // "Key frame is required" is WebCodecs' standard response when a
      // delta arrives before the first IDR — happens once per
      // subscribe-before-IDR window, fully recovered by the
      // requestKeyframe() below. Not actionable; debug-level so it
      // doesn't permanently sit in every session's warnings list.
      if (e instanceof DOMException && e.name === 'DataError' && /key frame/i.test(e.message)) {
        debug('[native-video-receiver] pre-IDR delta dropped, requesting keyframe', { err: e });
      } else {
        const repeat = this.failureLog.hit('decode');
        if (repeat !== null) warn(`[native-video-receiver] decode failed${repeat}`, { err: e });
      }
      this.requestKeyframe();
    }
  }

  private onFrame(frame: VideoFrame): void {
    // Decrement first so the in-flight count reflects reality even if
    // we short-circuit below (closed receiver, etc).
    this.inFlightCount = Math.max(0, this.inFlightCount - 1);
    if (this.closed) {
      frame.close();
      return;
    }
    // Replace the cached latest frame: close the old one before
    // overwriting so we don't leak GPU/CPU backing buffers (VideoFrame
    // holds a ref to the decoder output and is not refcounted by JS).
    const prev = this.latestFrame;
    this.latestFrame = frame;
    if (prev) prev.close();
    for (const [canvas, target] of this.targets) {
      this.paintTo(canvas, target, frame);
    }
  }

  /// Dispatch a paint to one target, preferring its WebGL pipeline.
  /// If the GL paint throws (e.g. context loss after a GPU driver
  /// reset, or a transient `INVALID_OPERATION` on resize) we don't
  /// try to recover for that target on the same frame — the GL
  /// canvas can't be downgraded to 2D in-place, and the next frame
  /// is at most ~33 ms away.
  private paintTo(
    canvas: HTMLCanvasElement,
    target: WebGLVideoTarget | null,
    frame: VideoFrame,
  ): void {
    if (target) {
      try {
        target.paint(frame);
      } catch (e) {
        const repeat = this.failureLog.hit('webgl-paint');
        if (repeat !== null) {
          warn(`[native-video-receiver] WebGL paint failed for this frame${repeat}`, { err: e });
        }
      }
    } else {
      this.paintFrame2D(canvas, frame);
    }
  }

  private paintFrame2D(canvas: HTMLCanvasElement, frame: VideoFrame): void {
    // displayWidth/Height is the intended display rect; codedWidth is
    // block-padded (16-aligned for H264) and would render squashed if
    // we sized the canvas to displayWidth then drew the full coded
    // buffer.
    const dw = frame.displayWidth || frame.codedWidth;
    const dh = frame.displayHeight || frame.codedHeight;
    if (dw !== canvas.width || dh !== canvas.height) {
      canvas.width = dw;
      canvas.height = dh;
    }
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;
    const vr = frame.visibleRect;
    try {
      if (vr) {
        ctx.drawImage(frame, vr.x, vr.y, vr.width, vr.height, 0, 0, dw, dh);
      } else {
        ctx.drawImage(frame, 0, 0, dw, dh);
      }
    } catch (e) {
      const repeat = this.failureLog.hit('draw-image');
      if (repeat !== null) warn(`[native-video-receiver] drawImage failed${repeat}`, { err: e });
    }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.pending = [];
    this.discardingUntilKey = false;
    this.inFlightCount = 0;
    for (const target of this.targets.values()) {
      target?.dispose();
    }
    this.targets.clear();
    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
    if (this.latestFrame) {
      this.latestFrame.close();
      this.latestFrame = null;
    }
    try {
      this.decoder?.close();
    } catch {
      // ignore
    }
    this.decoder = null;
  }
}

/** Compare two `VideoDecoderConfig.description` candidates byte-for-byte.
 *  Used by `configureDecoder` to detect "same SPS/PPS as the live
 *  decoder" so a redundant Config from a fresh IDR doesn't tear down
 *  a working decoder. We only ever pass `Uint8Array` here (the wire
 *  decoder always produces one) — the param type is widened to the
 *  WebCodecs `BufferSource` union for safety against future callers. */
function bytesEqual(
  a: AllowSharedBufferSource | undefined,
  b: AllowSharedBufferSource | undefined,
): boolean {
  if (!a || !b) return false;
  const av = toU8(a);
  const bv = toU8(b);
  if (av.byteLength !== bv.byteLength) return false;
  for (let i = 0; i < av.byteLength; i++) {
    if (av[i] !== bv[i]) return false;
  }
  return true;
}

function toU8(b: AllowSharedBufferSource): Uint8Array {
  if (b instanceof ArrayBuffer) return new Uint8Array(b);
  // ArrayBufferView (typed array / DataView) — cast through unknown
  // so the union with `SharedArrayBuffer`-backed views still typechecks.
  const v = b as unknown as { buffer: ArrayBufferLike; byteOffset: number; byteLength: number };
  return new Uint8Array(v.buffer as ArrayBuffer, v.byteOffset, v.byteLength);
}

function buildDecoderConfig(codec: Codec, description: Uint8Array | undefined): VideoDecoderConfig {
  const config: VideoDecoderConfig = {
    codec: codecString(codec),
    optimizeForLatency: true,
    // `no-preference`: WebKitGTK on Nvidia/Wayland accepts a
    // `prefer-hardware` config without erroring but then silently
    // never produces output (decoder stays "configured", `output`
    // callback never fires) — so we leave this on the platform's
    // default selection. If hardware decode works on a given host
    // WebCodecs picks it automatically; if not we get reliable
    // software decode rather than a black preview.
    hardwareAcceleration: 'no-preference',
  };
  if (description && description.byteLength > 0) {
    config.description = description;
  }
  return config;
}

/** WebCodecs codec strings. H264 uses a baseline-3.1 profile string by
 *  default; the actual SPS/PPS comes via `description`, so the codec
 *  string only needs to round-trip through the registry check. */
function codecString(codec: Codec): string {
  switch (codec) {
    case 'H264':
      return 'avc1.42E01F';
    case 'VP8':
      return 'vp8';
    case 'VP9':
      return 'vp09.00.10.08';
    case 'AV1':
      return 'av01.0.04M.08';
  }
}
